import { fromJS, Map } from 'immutable';

import {
  DefaultDataGenerator,
} from '@pearsontechnology/aero-plugin-schema';

import { getBoundReferencePaths } from 'utils/valueBinderUtil';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';

import { isImmutable } from 'pluginServices/helpers/immutableHelpers';
import activityService from 'services/ActivityService';
import interactiveService from 'services/InteractiveService';
import componentService from 'services/ComponentService';
import { INTERACTIVE, COMPONENT, ACTIVITY } from 'services/ActivityService/elementTypes';


import { BOUND_REFERENCES, RESOLVED } from './constants';

export default (() => {
  let _boundReferenceCache = Map();
  let _subscriptions = Map();
  const generator = new DefaultDataGenerator();

  const saveBoundReferences = (coursewareItemId, config) => {
    const immutableConfig = fromJS(config);
    const paths = getBoundReferencePaths(config);

    paths.forEach((path) => {
      if (path[0] !== 'stage') {
        _boundReferenceCache = _boundReferenceCache.setIn([coursewareItemId, ...path], immutableConfig.getIn(path));
      }
    });

    const cachePaths = getBoundReferencePaths(_boundReferenceCache.getIn([coursewareItemId]));
    cachePaths.forEach((path) => {
      if (!immutableConfig.getIn([...path, 'schemaProperty'])) {
        _boundReferenceCache = _boundReferenceCache.deleteIn([coursewareItemId, ...path]);
      }
    });
  };

  const getTargetValue = async (schema, previousSchema) => {
    const isBindingResolveToRealValueEnabled = featureFlagService.is(featureKeys.AUTHORING_BINDING_REAL_VALUE);

    if (isBindingResolveToRealValueEnabled) {
      // This if statement deals with the case where a user accidentially bind a field to itself. Thereby creating an infinate loop.
      if (previousSchema && previousSchema.sourceId === schema.sourceId && JSON.stringify(previousSchema.context) === JSON.stringify(schema.context)) {
        // eslint-disable-next-line no-console
        console.error(`Cannot bind a learner editable to itself! Please change binding on learner editable: ${schema.context} inside coureware ${schema.sourceId}`);
        return;
      }
      let coursewareItemType;
      switch(schema?.plugin?.type) {
        case 'lesson':
          coursewareItemType = ACTIVITY;
          break;
        case 'screen':
          coursewareItemType = INTERACTIVE;
          break;
        case 'component':
          coursewareItemType = COMPONENT;
          break;
        default:
      }

      if (!coursewareItemType) {
        // eslint-disable-next-line no-console
        console.error(`Unable to resolve bound references due to unknown coursewareItemType ${coursewareItemType}`);
        return [];
      }

      let targetConfig;
      if (coursewareItemType === ACTIVITY) {
        targetConfig = await activityService.getInActivityConfig(schema.sourceId);
      } else if (coursewareItemType === INTERACTIVE) {
        targetConfig = await interactiveService.getInInteractiveConfig(schema.sourceId);
      } else if (coursewareItemType === COMPONENT) {
        targetConfig = await componentService.getInComponentConfig(schema.sourceId);
      }

      const targetConfigImmutable = fromJS(targetConfig);
      let targetValue = targetConfigImmutable.getIn([...schema.context]);

      if (isImmutable(targetValue)) {
        targetValue = targetValue.toJS();
      }
      if (targetValue?.schemaProperty) {
        targetValue = await getTargetValue(targetValue, schema);
      }

      return targetValue;
    } else {
      const defaultValue = generator.generateDefaultData(schema);
      return defaultValue?.schemaProperty;
    }

  };

  const resolveBoundReferences = async (coursewareId, context, target, originalConfig) => {
    let resolvedConfig = fromJS(originalConfig);
    if (target === RESOLVED) {
      const storedReferences = _boundReferenceCache.getIn([coursewareId, ...context]);

      const paths = getBoundReferencePaths(storedReferences);
      if (!paths.length) return originalConfig;

      for (const path of paths) {
        let schema = storedReferences.getIn([...path]);
        if (schema && isImmutable(schema)) {
          schema = schema.toJS();
        }
        const targetValue = await getTargetValue(schema);

        resolvedConfig = resolvedConfig.setIn(
          [...path],
          isImmutable(targetValue) ? targetValue : fromJS(targetValue),
        );
      }

      return resolvedConfig.toJS();
    } else if (target === BOUND_REFERENCES) {
      const storedReferences = _boundReferenceCache.getIn([coursewareId, ...context]);
      if (storedReferences) {
        const paths = getBoundReferencePaths(storedReferences);

        if (!paths.length) return originalConfig;

        paths.forEach((path) => {
          resolvedConfig = resolvedConfig.setIn(
            [...path],
            storedReferences.getIn([...path])
          );
        });
        return resolvedConfig.toJS();
      }
      return originalConfig;
    }
    return originalConfig;
  };


  const _createUnsubscribe = (path, listenerId) => (
    () => {
      _subscriptions = _subscriptions.deleteIn([...path, listenerId]);
    }
  );

  const register = (path, listener, listenerId) => {
    _subscriptions = _subscriptions.setIn([...path, listenerId], listener);
    return _createUnsubscribe(path, listenerId);
  };

  const notify = (path, config) => {
    let data = { ...config };
    data = isImmutable(data) ? data.toJS() : data;

    const list = _subscriptions.getIn([...path]);
    if (list) {
      list.valueSeq().forEach((callback) => callback(data));
    }
  };

  const getTargetIdsFromConfig = (config) => {
    const boundReferencePaths = getBoundReferencePaths(config);
    const targetIds = [];
    const configImmutable = isImmutable(config) ? config : fromJS(config);

    for (const path of boundReferencePaths) {
      const targetId = configImmutable.getIn([...path, 'sourceId']);

      if (!targetIds.includes[targetId]) {
        targetIds.push(targetId);
      }
    }

    return targetIds;
  };

  const getIn = (id, path = []) =>  _boundReferenceCache.getIn([id, ...path]);

  return {
    saveBoundReferences,
    resolveBoundReferences,
    getIn,
    getStored: () => Object.assign({}, _boundReferenceCache.toJS()),
    clearStored: () => { _boundReferenceCache = Map(); },
    register,
    notify,
    getTargetIdsFromConfig,
  };
})();
